import React, { useMemo } from "react";
import { CheckboxComponent } from "@common/components/checkbox/CheckboxComponent";
import { IState } from "@common/state/IState";
import {
    umSpPageDataSelector,
    umSpPageSchemeSelector,
    umSpPageSubscriptionsTypesConsentsExtractor,
} from "../_shared/selectors";
import { useObjectSelector } from "@common/utils/selectors/useSelectorEx";
import { useDispatch, useSelector } from "react-redux";
import {
    ISubscriptionInfo,
    IUserSubscription,
} from "../subscriptionPreferences/SubscriptionPreferencesModel";
import { toggleSubscriptionConsentOnSubscriptionPreferencesAction } from "../subscriptionPreferences/subscriptionPreferencesActions";
import { SubscriptionsTableErrorElement } from "../subscriptionsTableErrors/SubscriptionsTableErrorElement";

const i18n = {
    head_smsconsent: "SMS",
    head_emailconsent: "Email",
    head_pushconsent: "Push",
    head_postconsent: "Post",
};

function selector(s: IState) {
    const data = umSpPageDataSelector(s);
    if (!data)
        return {
            consentsTypesMap: {},
            consentsTypesKeys: [],
            titles: [],
            subscriptions: [],
        };

    const [
        consentsTypesMap,
        consentsTypesKeys,
    ] = umSpPageSubscriptionsTypesConsentsExtractor(
        data.subscriptionsTypes,
        umSpPageSchemeSelector(s)
    );

    return {
        subscriptions: Object.keys(data.subscriptions).map(key => ({
            key,
            subscriptionTypeId: data.subscriptions[key].subscription,
        })),
        consentsTypesMap,
        consentsTypesKeys,
        titles: [null].concat(consentsTypesKeys.map(k => i18n[`head_${k}`])),
    };
}

export function SubscriptionsTableElement({ el }) {
    const {
        subscriptions,
        titles,
        consentsTypesMap,
        consentsTypesKeys,
    } = useObjectSelector(selector);

    return (
        <>
            <div className={el("table-head")}>
                {titles.map(key => (
                    <div key={key} children={key} />
                ))}
            </div>

            <div className={el("table-body")}>
                {subscriptions.map(({ key, subscriptionTypeId }, index) => (
                    <React.Fragment key={key}>
                        <TableRow
                            index={2 * index}
                            consentKey={key}
                            subscriptionTypeId={subscriptionTypeId}
                            consentsTypesMap={consentsTypesMap}
                            consentsTypesKeys={consentsTypesKeys}
                            el={el}
                        />
                        <SubscriptionsTableErrorElement
                            path={key}
                            index={index}
                            consentsTypesKeys={consentsTypesKeys}
                            className={el("table-error")}
                        />
                    </React.Fragment>
                ))}
            </div>
        </>
    );
}

const consentSelector = key => (s: IState) => {
    const data = umSpPageDataSelector(s);
    return {
        consent: data?.subscriptions[key] as IUserSubscription,
        generalConsents: data?.generalConsents,
    };
};

function TableRow({
    consentsTypesMap,
    consentsTypesKeys,
    consentKey,
    el,
    index,
    subscriptionTypeId,
}) {
    const { consent, generalConsents } = useSelector(
        consentSelector(consentKey)
    );
    const subscription: ISubscriptionInfo =
        consentsTypesMap[consent.subscription];

    const [row, firstCell] = useMemo(
        () => [
            {
                gridRow: `${index + 1} / span 2`,
                gridColumn: `1 / span ${consentsTypesKeys.length + 1}`,
            },
            { gridRow: index + 1, gridColumn: 1 },
        ],
        [index]
    );

    return (
        <>
            <div style={row} className={el("table-line")} />

            <div style={firstCell} className={el("cell")}>
                <div className={el("cell-title")}>{subscription.name}</div>
                <div className={el("cell-content")}>
                    {subscription.description}
                </div>
            </div>

            {consentsTypesKeys.map((key, colIndex) => (
                <Box
                    key={`${key}_${colIndex}:${index}`}
                    index={index}
                    gridColumn={colIndex + 2}
                    checked={consent[key]}
                    active={generalConsents[key]}
                    consentKey={key}
                    consentId={consent.id}
                    subscriptionTypeId={subscriptionTypeId}
                />
            ))}
        </>
    );
}

function Box({
    index,
    gridColumn,
    checked,
    active,
    consentKey,
    consentId,
    subscriptionTypeId,
}) {
    const style = useMemo(() => ({ gridRow: index + 1, gridColumn }), [
        index,
        gridColumn,
    ]);
    const dispatch = useDispatch();
    const [toggle, dataKey] = useMemo(
        () => [
            () => {
                dispatch(
                    toggleSubscriptionConsentOnSubscriptionPreferencesAction(
                        consentKey,
                        consentId,
                        !checked
                    )
                );
            },
            consentKey + "_" + consentId,
        ],
        [consentKey, consentId, checked]
    );

    return (
        <div style={style} data-key={dataKey} data-type-id={subscriptionTypeId}>
            <CheckboxComponent
                checked={checked}
                disabled={!active}
                onChange={toggle}
            />
        </div>
    );
}
